import {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  FC,
  PropsWithChildren,
} from "react";

import firebase from "firebase/compat/app";

import { ApolloProvider } from "@apollo/client";
import * as Sentry from "@sentry/react";
import useSearchQuery from "hooks/useSearchQuery";
import { MeQuery, useMeLazyQuery } from "lib/apollo-v2/generated/graphql";
import clientV2 from "lib/apollo-v2";
export const UserContext = createContext<{
  user: any;
  setUser: (user: any) => void;
  firebaseUser: firebase.User | null;
  loading: boolean;
  location: Location | null;
  preferLang: string | null;
  refreshUser: () => Promise<void>;
}>({
  user: null,
  setUser: () => {},
  firebaseUser: null,
  loading: false,
  location: null,
  preferLang: null,
  refreshUser: () => Promise.resolve(),
});
export const UserUpdateContext = createContext({});

export const useUser = () => useContext(UserContext);
export const useSignOutUser = () => useContext(UserUpdateContext);

const getFirebaseTokenFromLocalStorage = () => localStorage?.getItem("token");
const removeFirebaseTokenFromLocalStorage = () =>
  localStorage.removeItem("token");

const DEFAULT_STATE = {
  firebase: {
    loading: true,
    data: null,
  },
  database: {
    loading: false,
    data: null,
  },
  verify: {
    loading: false,
  },
  preferLang: {
    loading: false,
    data: null,
  },
};

const NO_LOADING_STATE = {
  firebase: {
    loading: false,
    data: null,
  },
  database: {
    loading: false,
    data: null,
  },
  verify: {
    loading: false,
  },
  preferLang: {
    loading: false,
    data: null,
  },
};

/**
 * Module-scoped mutable container for the pending refreshUser resolver.
 * Kept outside the component so the React compiler does not restrict mutations.
 * Safe because UserProvider is a singleton rendered once at the app root.
 */
const refreshResolverBox: { current: (() => void) | null } = { current: null };

const UserProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  const [state, setState] = useState(DEFAULT_STATE);
  const [lngParam] = useSearchQuery("lng");

  // Backend data of user
  const [getUserProfile, userProfile] = useMeLazyQuery({
    client: clientV2,
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      userProfileCallback(data);
      // setState((state) => ({
      //   ...state,
      //   verify: {
      //     loading: false,
      //   },
      //   database: {
      //     loading: false,
      //     data: data,
      //   },
      // }));

      if (lngParam) {
        setState((state) => ({
          ...state,
          preferLang: {
            loading: false,
            data: { language: lngParam },
          },
        }));
      } else {
        // TODO: remove this when we have the preferred language in the user profile
        // preferredLangCallback(data.result.preferredLanguage);
      }
    },
    onError: () => {
      setState(NO_LOADING_STATE);
      const resolver = refreshResolverBox.current;
      // clear the token from localStorage
      localStorage.removeItem("token");
      if (resolver) {
        refreshResolverBox.current = null;
        resolver();
      }
    },
  });

  // Preferred Language of user
  // const [getUserPreferredLanguage, preferredLang] = useLazyQuery(
  //   QUERIES.USER.PREFERRED_LANGUAGE,
  //   {
  //     client: client,
  //     fetchPolicy: "no-cache",
  //     onCompleted: (data) => {
  //       preferredLangCallback(data);
  //     },
  //     onError: () => {
  //       setState(NO_LOADING_STATE);
  //     },
  //   },
  // );

  // Sign out user function
  const resetUserData = () => {
    setState(NO_LOADING_STATE);
    window.location.reload();
  };

  // refreshResolverBox is declared at module scope above.

  /**
   * Manually re-sync UserProvider state with Firebase + localStorage.
   * Call this after storing the JWT token in localStorage during auth
   * flows that skip a full page reload (e.g. the booking-gate onSuccess path).
   * Returns a Promise that resolves once the backend profile has loaded.
   */
  const refreshUser = useCallback((): Promise<void> => {
    const currentFirebaseUser = firebase.auth().currentUser;
    const token = getFirebaseTokenFromLocalStorage();

    if (!currentFirebaseUser || !token) {
      return Promise.resolve();
    }

    setState((prev) => ({
      ...prev,
      firebase: {
        loading: false,
        data: currentFirebaseUser,
      },
      database: {
        loading: true,
        data: prev.database.data,
      },
      verify: {
        loading: false,
      },
    }));

    const promise = new Promise<void>((resolve) => {
      refreshResolverBox.current = resolve;
    });
    getUserProfile();
    return promise;
  }, [getUserProfile]);

  // Set Firebase user
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({
      ...DEFAULT_STATE,
      firebase: {
        loading: true,
        data: null,
      },
    });

    const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
      const token = getFirebaseTokenFromLocalStorage();
      if (user && token) {
        setState({
          ...DEFAULT_STATE,
          firebase: {
            loading: false,
            data: user,
          },
          verify: {
            // TODO: behaviour needs watching
            loading: false,
          },
        });
      } else {
        // Sign out user
        removeFirebaseTokenFromLocalStorage();
        setState(NO_LOADING_STATE);
      }
    });

    return () => unsubscribe();
  }, []);

  // Call verify user token
  useEffect(() => {
    if (state.firebase.loading) return;
    if (!state.firebase.data) return;
    const token = getFirebaseTokenFromLocalStorage();
    if (!token) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState(NO_LOADING_STATE);
      return;
    }
    getUserProfile();
  }, [state.firebase, getUserProfile]);

  // Sentry User set
  const setSentryIdentifyUser = (data) => {
    Sentry.setUser({
      email: data?.email,
      name: data?.firstName,
    });
  };

  //Set Google Tag Manager DataLayer
  const sendUserToDataLayer = (data) => {
    const userData = { ...data };
    delete userData["__typename"];
    // @ts-expect-error we do not have access for type of window
    window.dataLayer = window.dataLayer || [];
    // @ts-expect-error we do not have access for type of window
    window.dataLayer.push({ event: "userLoggedIn", user: userData });
  };

  // Set database user
  const userProfileCallback = useCallback(
    (data: MeQuery) => {
      // if (!data || !data.result || data.result.__typename !== "Contact") return;

      // Set Provider Data
      setState((state) => ({
        ...state,
        database: {
          loading: false,
          data: data.result,
        },
        // preferLang: {
        //   loading: false,
        //   data: data.result.preferredLanguage,
        // },
      }));

      // Set Sentry user
      setSentryIdentifyUser(data.result);

      // Send user data to dataLayer
      sendUserToDataLayer(data.result);

      // Resolve any pending refreshUser promise
      const resolver = refreshResolverBox.current;
      if (resolver) {
        refreshResolverBox.current = null;
        resolver();
      }
    },
    [],
  ); // dependency on userProfile just for error check, though data is preferred

  return (
    <UserContext.Provider
      value={{
        user: state.database.data,
        setUser: (newUser) =>
          setState((state) => ({
            ...state,
            database: {
              loading: false,
              data: newUser,
            },
          })),
        firebaseUser: state.firebase.data,
        loading:
          state.firebase.loading ||
          state.database.loading ||
          state.verify.loading ||
          state.preferLang.loading,
        location: null,
        preferLang: state.preferLang.data,
        refreshUser,
      }}
    >
      <UserUpdateContext.Provider value={resetUserData}>
        <ApolloProvider client={clientV2}>{children}</ApolloProvider>
      </UserUpdateContext.Provider>
    </UserContext.Provider>
  );
};

export default UserProvider;
